/**
 * **Lepas perangkat ini** — memutus pasangan supaya tablet/HP bisa dipasangkan ke outlet atau
 * peran yang LAIN.
 *
 * ### ⛔ Lubang yang ditutupnya (Imron, 2026-08-23)
 * *"HP saya kalau mau coba sebagai KDS di PB-PKB bisa? Saat ini keluar sebagai kasir Seribu Rasa."*
 *
 * Bentuk layar ditentukan **pendaftaran perangkat** (K21) — dan itu benar. Tapi sampai kini tak ada
 * satu pun cara MELEPAS pasangan dari dalam aplikasi: `ClearCacheButton` sengaja menyimpan
 * pasangannya ("tablet tak perlu di-pair ulang"), dan itu juga benar untuk kasusnya. Akibat
 * gabungannya: perangkat yang sudah dipasangkan **terkunci selamanya** ke outlet & peran
 * pertamanya, dan satu-satunya jalan keluar adalah membuka DevTools — persis yang layar pemasangan
 * ada untuk menghapusnya.
 *
 * ### ⛔⛔ Bergerbang PIN administrator, seperti membersihkan salinan
 * Melepas pasangan membuat terminal berhenti berjualan sampai seseorang memasangkannya lagi. Itu
 * bukan tombol yang boleh tersenggol di tengah jam sibuk.
 *
 * ⚠️ Antrean penjualan (`outbox`) DIPERIKSA lebih dulu: melepas pasangan selagi ada penjualan yang
 * belum diakui portal berarti membuang uang yang sudah diterima dari tamu — dan tak ada satu pun
 * jejak yang tersisa untuk membandingkannya.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { lupakanDevicePairing, lupakanToken } from '../api/session';
import { pendingCount } from '../api/outbox';

export function UnpairButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [staffCode, setStaffCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const menunggu = pendingCount();

  const lepas = async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * ⛔ Izinnya diperiksa dari JAWABAN SERVER, bukan dari sesi yang sedang berjalan: yang
       * membuktikan kehadiran administrator saat ini adalah PIN yang baru saja diketik.
       */
      const r = await api.loginPin({ staffCode: staffCode.trim(), pin });
      if (!r.permissions.includes('master:edit')) {
        setError(t('cashier.unpair.needsPermission'));
        return;
      }
      lupakanDevicePairing();
      lupakanToken();
      /* ⚠️ Muat ulang: seluruh layar lahir dari identitas perangkat, dan menyusunnya ulang di
         tempat berarti separuh komponen memegang identitas yang sudah tak ada. */
      location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="action-secondary"
        data-testid="unpair-open"
        onClick={() => { setOpen(true); }}
      >
        {t('cashier.unpair.button')}
      </button>

      {open && (
        <div className="dialog" role="dialog" data-testid="unpair-dialog">
          <h2>{t('cashier.unpair.title')}</h2>
          <p className="dialog-note">{t('cashier.unpair.what')}</p>

          {/*
            ⛔ Antrean penjualan MENGHALANGI. Melepas pasangan selagi ada penjualan yang belum
            diakui portal berarti membuang uang yang sudah diterima dari tamu.
          */}
          {menunggu > 0 ? (
            <p className="error" data-testid="unpair-pending">
              {t('cashier.unpair.pending', { n: menunggu })}
            </p>
          ) : (
            <>
              <input
                data-testid="unpair-staff"
                placeholder={t('cashier.unpair.staffCode')}
                value={staffCode}
                onChange={(e) => { setStaffCode(e.target.value); }}
              />
              <input
                data-testid="unpair-pin"
                type="password"
                inputMode="numeric"
                placeholder={t('cashier.unpair.pin')}
                value={pin}
                onChange={(e) => { setPin(e.target.value); }}
              />
            </>
          )}

          {error !== null && <p className="error" data-testid="unpair-error">{error}</p>}

          <div className="dialog-actions">
            <button type="button" data-testid="unpair-cancel" onClick={() => { setOpen(false); }}>
              {t('cashier.unpair.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="unpair-confirm"
              disabled={busy || menunggu > 0 || staffCode.trim() === '' || pin === ''}
              onClick={() => void lepas()}
            >
              {t('cashier.unpair.confirm')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
